import { logStepIdx, logStepEdge } from './chartUtils'
// ============================================
// candleHelpers.js
//
// Single-format candle representation. Three states a candle can be in:
//
//   1. Live (receiving ticks):
//        _liveLevels:  Map<int stepIdx, {buy, sell}>
//        _nativeBucket: number (bucket size stepIdx is denominated in)
//        _native:       null
//        _display, _displayBucket: lazily built on render
//
//   2. Packed (no longer receiving ticks):
//        _liveLevels:  null
//        _native:       Float64Array of [stepIdx, buy, sell, ...] sorted by stepIdx
//        _nativeBucket: number
//        _nativeTotalVol, _nativeDelta: cached scalars
//        _display, _displayBucket: lazily built on render
//
//   3. Historical (OHLCV-only seed from backend, no per-level data): isHistorical=true
//      EXCEPTION: _omFootprint historical candles (OpenMarket volume-profile
//      history on futures) carry packed per-level data like state 2 — they are
//      isHistorical (never rebuilt from the live trade buffer) but DO build a
//      _display and paint cells.
//
// Why this shape:
//   • stepIdx is integer = floor(mcap / _nativeBucket). Keys are int, not
//     stringified mcap, so accumulation is hash-free / GC-cheap.
//   • _native uses Float64 (not Float32) so $1B mcap with $1 buckets still
//     resolves — Float32's ~7-digit mantissa silently collides at that scale.
//   • _display caches a render-ready Float32Array of [mcap, buy, sell, ...]
//     at the current displayBucket. Bucket flips invalidate _display only;
//     _native is untouched. Rebuild is a streaming integer aggregation
//     over sorted _native — zero per-level allocation.
//
// Display rebuild rules (in buildDisplay):
//   displayBucket >= _nativeBucket → streaming coarsen, lossless.
//   displayBucket <  _nativeBucket → can't refine from packed; clamp _display
//                                    to _nativeBucket. Caller is expected
//                                    to rawTrades-replay candles within the
//                                    rawTrades window before render.
//
// All readers go through forEachLevel / candleTotalVolume / candleDelta /
// candleMaxLevelVolume / candleLevelCount / hasMcapLevels — they read from
// _display preferentially, with live and native as fallbacks.
// ============================================

export const PACK_BUFFER_CANDLES = 5

// Snap an mcap to integer stepIdx for a given bucket. Mirrors snapBucket
// integer-space math from chartUtils so step boundaries align identically.
export const stepIdxOf = (mcap, bucket) => {
  if (bucket >= 1) return Math.floor(mcap / bucket)
  let p = 0
  while (p < 15) {
    const scaled = bucket * Math.pow(10, p)
    if (Math.abs(scaled - Math.round(scaled)) < 1e-9) break
    p++
  }
  const factor = Math.pow(10, p)
  const sBucket = Math.round(bucket * factor)
  const sValue  = Math.floor(mcap * factor + 1e-9)
  return Math.floor(sValue / sBucket)
}

// Build a fresh live candle. nativeBucket is locked at birth.
export const newLiveCandle = (startTime, nativeBucket, extras = {}) => ({
  startTime,
  isHistorical:    false,
  open_mcap:       extras.open_mcap  ?? null,
  close_mcap:      extras.close_mcap ?? null,
  high_mcap:       extras.high_mcap  ?? null,
  low_mcap:        extras.low_mcap   ?? null,
  volume_tokens:   extras.volume_tokens ?? null,
  _liveLevels:     new Map(),
  _nativeBucket:   nativeBucket,
  _native:         null,
  _nativeTotalVol: 0,
  _nativeDelta:    0,
  _display:        null,
  _displayBucket:  null,
  // Version-counter invalidation. addTickToLive / packCandle / rebucket
  // increment _dataVersion; buildDisplay caches by (_displayBucket,
  // _displayVersion) and skips the rebuild when both match. Lets idle
  // redraws (hover, crosshair, x-pan) reuse the cached _display Float64.
  _dataVersion:    0,
  _displayVersion: -1,
  // Multi-slot display cache — see buildDisplay. Map<displayBucket, arr>,
  // capped at MAX_DISPLAY_SLOTS, dropped wholesale when _dataVersion moves.
  // _display always points at one of these entries, never a copy.
  _dispCache:      null,
  _dispCacheVer:   -1,
})

// Add a tick to a live candle. O(1) common path. Invalidates _display.
// If the candle is already packed (late tick after pack pass), unpack
// _native into _liveLevels first so the new tick merges with prior data.
export const addTickToLive = (c, mcap, side, amount) => {
  if (c._native && !c._liveLevels) {
    // Late tick to a packed candle — unpack into _liveLevels.
    const map = new Map()
    const a = c._native
    for (let i = 0; i < a.length; i += 3) {
      map.set(a[i], { buy: a[i + 1], sell: a[i + 2] })
    }
    c._liveLevels     = map
    c._native         = null
    c._nativeTotalVol = 0
    c._nativeDelta    = 0
  }
  if (!c._liveLevels) c._liveLevels = new Map()
  const step = stepIdxOf(mcap, c._nativeBucket)
  let lvl = c._liveLevels.get(step)
  if (!lvl) { lvl = { buy: 0, sell: 0 }; c._liveLevels.set(step, lvl) }
  if (side === 'buy') lvl.buy  += amount
  else                lvl.sell += amount
  // Bump version instead of nulling _display. buildDisplay compares
  // _displayVersion === _dataVersion to detect staleness, so the cached
  // Float64 array survives until the next render that actually needs it.
  c._dataVersion = (c._dataVersion | 0) + 1
}

// Pack a live candle: _liveLevels → _native Float64Array (sorted by step).
// Idempotent. After pack, _liveLevels is null and the candle is read-only
// for level data (further ticks would need to go into a fresh candle).
export const packCandle = (c, _bucketSize) => {
  if (!c) return
  if (c._native && !c._liveLevels) return
  const map = c._liveLevels
  if (!map || map.size === 0) {
    c._native          = new Float64Array(0)
    c._nativeTotalVol  = 0
    c._nativeDelta     = 0
    c._liveLevels      = null
    c._dataVersion     = (c._dataVersion | 0) + 1
    return
  }
  // Sort steps ascending so coarsening can stream-aggregate.
  const steps = Array.from(map.keys()).sort((a, b) => a - b)
  const arr = new Float64Array(steps.length * 3)
  let totalVol = 0, delta = 0
  let oi = 0
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    const lvl  = map.get(step)
    const buy  = lvl.buy  || 0
    const sell = lvl.sell || 0
    arr[oi++] = step
    arr[oi++] = buy
    arr[oi++] = sell
    totalVol += buy + sell
    delta    += buy - sell
  }
  c._native         = arr
  c._nativeTotalVol = totalVol
  c._nativeDelta    = delta
  c._liveLevels     = null
  c._dataVersion    = (c._dataVersion | 0) + 1
}

// Scratch for the spread path below: rows accumulate here as
// [rowIdx, buy, sell, ...] and the used prefix is copied out to the final
// _display once the count is known. Module-level so steady state allocates
// nothing but the output; grown geometrically on the rare candle that
// exceeds it. Safe as a shared buffer because buildDisplay is synchronous
// and never re-enters — the whole spread completes before anything else can
// touch this.
let _spreadScratch = new Float64Array(3 * 1024)

// Build _display Float64Array of [mcap, buy, sell, ...] at displayBucket.
// Idempotent — short-circuits when (_displayBucket, _displayVersion) match
// what's already cached. Streaming aggregation, zero per-level object
// allocation on either path. Float64 (not Float32) so $1B mcap with $1
// buckets still resolves at the renderer; Float32's ~7-digit mantissa
// silently collides at that scale and was the reason _native is Float64.
// The BUILDER. Always regroups from _native/_liveLevels — no cache probe of
// its own beyond the active-slot short-circuit. buildDisplay (below) wraps
// it with the multi-slot cache; call that, not this.
// ── PROBE [disp]. localStorage.cheddar_debug_disp = '1'. Delete after. ──
// One line per DISTINCT (nativeBucket, displayBucket) pair, not per candle —
// scrolling 500 candles gives a handful of lines. Answers three things at once:
//
//   nb      the native bucket THIS candle froze at close. Candles hold
//           heterogeneous values against one global db, so several lines here
//           means old and new candles are on different grids.
//   ratio   nb/db. Above 1 the cell spans several display rows and needs the
//           spread path; above 512 it CANNOT have it.
//   path    dsOf   — native finer than display, floor(mcap/db). Fine.
//           same   — identical lattices, short-circuit.
//           spread — proportional overlap, continuous.
//           CLAMP  — past the 512 cap: whole volume dumped in one row.
//                    "clumped but alive". This is what snapping looks like.
//   n       how many candles took that path.
const _dispProbe = new Map()
const _dispProbeLog = (nb, db, ratio, path) => {
  try {
    if (typeof localStorage === 'undefined') return
    if (localStorage.getItem('cheddar_debug_disp') !== '1') return
  } catch { return }
  const k = nb + '|' + db + '|' + path
  const hit = (_dispProbe.get(k) || 0) + 1
  _dispProbe.set(k, hit)
  // Log on first sight, then at 10x intervals so a hot path stays visible
  // without flooding.
  if (hit === 1 || hit === 10 || hit === 100 || hit === 1000) {
    console.log(`[disp] nb=${nb} db=${db} ratio=${Number.isFinite(ratio) ? ratio.toFixed(3) : ratio} path=${path} n=${hit}`)
  }
}

const _buildDisplayInto = (c, displayBucket, displayRatio) => {
  // _omFootprint exception: OpenMarket history candles are isHistorical
  // (the live-rebuild machinery must leave them alone) but carry packed
  // per-level data and DO render cells — so they build displays like any
  // packed candle. Plain OHLCV historical candles still return here.
  if (!c || (c.isHistorical && !c._omFootprint)) return
  const dataVer = c._dataVersion | 0
  if (c._display && c._displayBucket === displayBucket
      && c._displayRatio === displayRatio
      && c._displayVersion === dataVer) return

  const nativeBucket = c._nativeBucket
  if (!nativeBucket) {
    c._display        = null
    c._displayBucket  = null
    c._displayRatio   = null
    c._displayVersion = -1
    c._dispCache      = null
    return
  }
  // Map every cell by MCAP onto the display lattice: ds = floor(mcap / db).
  // Output mcaps are always multiples of displayBucket, so they land on
  // the renderer's row grid no matter what this candle's nativeBucket is.
  // Candles freeze their nativeBucket at close time, so the array holds
  // heterogeneous nb values (0.006 … 30) against ONE global db — the old
  // step-ratio math (floor(step/ratio) with a ratio<1 clamp to native
  // spacing) emitted off-lattice mcaps for any candle whose nb didn't
  // cleanly divide db, and the renderer's row lookup missed them: cells
  // existed in _native/_liveLevels but drew nothing. When db < nb a
  // native cell can't be split (no raw trades here); its full volume
  // lands in the display row containing its mcap. Volume conserved.
  // EPS absorbs fp error in step*nb/db at exact row boundaries.
  const EPS = 1e-9
  const dsOf = (step) => Math.floor((step * nativeBucket) / displayBucket + EPS)
  // LOG: every fast path below assumes ONE arithmetic lattice shared by
  // _native and _display (same spacing, or an integer multiple of it). A
  // geometric display lattice shares neither, so log always takes the general
  // overlap path. That is a real cost — no sameLattice short-circuit, no
  // integer coarsen — and it is the correct trade: overlap is the only
  // formulation that is exact when the target rows are not uniform.
  const LOG = !!(displayRatio && displayRatio > 1)
  const sameLattice = !LOG && nativeBucket === displayBucket

  // Empty-but-truthy _liveLevels must not shadow a populated _native —
  // restored/rebucketed candles can carry both (newLiveCandle seeds an
  // empty Map, IDB restore attaches _native). Route by where data IS.
  const liveMap   = c._liveLevels
  const hasLive   = !!(liveMap && liveMap.size > 0)
  const hasNative = !!(c._native && c._native.length > 0)

  // ── Spread path: native coarser than display ──────────────────────
  // A coarse cell spans several display rows. Dumping its whole volume
  // into the single row containing its low edge leaves the rest of its
  // span EMPTY — sparse columns with gaps when zooming into stored
  // candles. Distribute proportionally by overlap instead: continuous
  // volume, conserved totals, position-approximate (true sub-bucket
  // distribution is unknowable without raw trades).
  // BOUNDED: rows-per-cell capped at 512. Degenerate ratios (stored
  // bucket 30 vs display bucket 1e-4 → millions of rows per cell) blew
  // the accumulator Map into millions of entries and crashed the first
  // enumerator downstream (RangeError: Too many properties to
  // enumerate). Past the cap, fall through to the single-row path —
  // clumped but alive; at 500x coarser than the view the spread is
  // visual mush regardless.
  const spreadRatio = nativeBucket / displayBucket
  _dispProbeLog(nativeBucket, displayBucket, spreadRatio,
    LOG ? 'spread(log)'
    : sameLattice ? 'same'
    : spreadRatio <= 1 + EPS ? 'dsOf'
    : spreadRatio <= 512 ? 'spread'
    : 'CLAMP')
  // Log enters here unconditionally: a native cell can straddle several
  // geometric rows at the bottom of the range and sit inside one at the top,
  // so "is it coarser?" is not a single number and the overlap loop handles
  // both directions anyway. The 512 cap still applies to the LINEAR case,
  // where it bounds the row fan-out per native cell.
  if ((LOG || (!sameLattice && spreadRatio > 1 + EPS && spreadRatio <= 512)) && (hasLive || hasNative)) {
    // STREAMED, like the three paths below it. This used to be the odd one
    // out: a Map<row, [buy, sell]> accumulator, one array allocated per
    // display row, then Array.from(keys).sort() to put the rows in order —
    // for output that is already monotonic by construction. It is also the
    // zoom-IN rebuild, the direction whose convergence you can see, so it
    // was paying the most where it mattered most.
    //
    // Why a single pass is exact. Native cells are disjoint intervals
    // [step·nb, (step+1)·nb). Each fans out into a CONTIGUOUS, ASCENDING run
    // of display rows [r0, rEnd). With cells visited in ascending step order,
    // cell i+1's r0 = floor(hi_i/db) while cell i's last row is
    // ceil(hi_i/db) − 1, so r0_{i+1} ≥ lastRow_i with equality exactly when
    // hi_i/db is non-integral — one shared boundary row, never more. So rows
    // arrive non-decreasing, and the only merge that can ever occur is
    // "this row equals the one just written". One carry, no map, no sort.
    //
    // Bit-identical to the old path, not merely close: a display row is
    // narrower than a native cell, so it can intersect at most two of them.
    // Two terms commute exactly in IEEE-754; the old (0 + x) + y and the
    // new x + y produce the same double. The harness demanded equality,
    // not tolerance.
    //
    // Ascending order is guaranteed for _native by packCandle (sorted at
    // pack time) and bought for a live map by sorting its keys once — the
    // same sort the live-coarsen path below already pays, and it sorts
    // INPUT steps, which the spread makes fewer than output rows.
    let s = _spreadScratch
    let oi = 0
    let lastRow = NaN
    const spreadCell = (step, buy, sell) => {
      const lo = step * nativeBucket
      const hi = lo + nativeBucket
      // Row extent of this native cell. Linear rows are r*db wide; log rows
      // are [ratio^r, ratio^(r+1)) on the SAME global anchored-at-1 lattice
      // the grid memo snaps to. Everything below — the overlap, the
      // proportional split, the monotonic single-carry output — is unchanged,
      // because interval intersection never cared whether the target rows were
      // uniform.
      let r, rEnd
      if (LOG) {
        if (!(lo > 0)) return           // ln of a non-positive price; not a real level
        r = logStepIdx(lo, displayRatio)
        rEnd = logStepIdx(hi, displayRatio) + 1
      } else {
        r = Math.floor(lo / displayBucket + EPS)
        rEnd = Math.ceil(hi / displayBucket - EPS)
      }
      for (; r < rEnd; r++) {
        const rowLo = LOG ? logStepEdge(r, displayRatio) : r * displayBucket
        const rowHi = LOG ? logStepEdge(r + 1, displayRatio) : rowLo + displayBucket
        const ov = Math.min(hi, rowHi) - Math.max(lo, rowLo)
        if (ov <= 0) continue
        const f = ov / nativeBucket
        if (r === lastRow) {
          s[oi - 2] += buy * f
          s[oi - 1] += sell * f
        } else {
          if (oi + 3 > s.length) {
            const g = new Float64Array(s.length * 2)
            g.set(s)
            s = _spreadScratch = g
          }
          s[oi++] = r
          s[oi++] = buy * f
          s[oi++] = sell * f
          lastRow = r
        }
      }
    }
    if (hasLive) {
      const steps = Array.from(liveMap.keys()).sort((a, b) => a - b)
      for (let i = 0; i < steps.length; i++) {
        const lvl = liveMap.get(steps[i])
        spreadCell(steps[i], lvl.buy || 0, lvl.sell || 0)
      }
    } else {
      const a = c._native
      for (let i = 0; i < a.length; i += 3) spreadCell(a[i], a[i + 1], a[i + 2])
    }
    const arr = new Float64Array(oi)
    for (let i = 0; i < oi; i += 3) {
      arr[i]     = LOG ? logStepEdge(s[i], displayRatio) : s[i] * displayBucket
      arr[i + 1] = s[i + 1]
      arr[i + 2] = s[i + 2]
    }
    c._display        = arr
    c._displayBucket  = displayBucket
    c._displayRatio   = displayRatio
    c._displayVersion = dataVer
    return
  }

  // Live path — sort once, then stream-aggregate. Avoids the temp
  // Map<ds, [buy, sell]> + per-distinct-ds 2-element array that the
  // old coarsen path built (M sets + D allocs + 2nd sort of D keys).
  if (hasLive) {
    const map = liveMap
    const steps = Array.from(map.keys())
    steps.sort((a, b) => a - b)

    if (sameLattice) {
      const arr = new Float64Array(steps.length * 3)
      let oi = 0
      for (let i = 0; i < steps.length; i++) {
        const s = steps[i]
        const lvl = map.get(s)
        arr[oi++] = s * displayBucket
        arr[oi++] = lvl.buy  || 0
        arr[oi++] = lvl.sell || 0
      }
      c._display        = arr
      c._displayBucket  = displayBucket
      c._displayRatio   = displayRatio
      c._displayVersion = dataVer
      return
    }

    // Two-pass: count distinct dispSteps, then fill. steps sorted
    // ascending → mcap ascending → ds monotonic non-decreasing for any
    // nb/db ratio, so boundary-emit streaming holds.
    let outCount = 0, last = NaN
    for (let i = 0; i < steps.length; i++) {
      const ds = dsOf(steps[i])
      if (ds !== last) { outCount++; last = ds }
    }
    const arr = new Float64Array(outCount * 3)
    let oi = 0, curDs = NaN, curBuy = 0, curSell = 0
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i]
      const lvl  = map.get(step)
      const ds   = dsOf(step)
      if (ds !== curDs) {
        if (!Number.isNaN(curDs)) {
          arr[oi++] = curDs * displayBucket
          arr[oi++] = curBuy
          arr[oi++] = curSell
        }
        curDs = ds; curBuy = lvl.buy || 0; curSell = lvl.sell || 0
      } else {
        curBuy  += lvl.buy  || 0
        curSell += lvl.sell || 0
      }
    }
    if (!Number.isNaN(curDs)) {
      arr[oi++] = curDs * displayBucket
      arr[oi++] = curBuy
      arr[oi++] = curSell
    }
    c._display        = arr
    c._displayBucket  = displayBucket
    c._displayRatio   = displayRatio
    c._displayVersion = dataVer
    return
  }

  // Native path — _native is sorted ascending by step from packCandle,
  // so the same mcap-lattice streaming applies.
  if (hasNative) {
    const a = c._native
    if (sameLattice) {
      const arr = new Float64Array(a.length)
      for (let i = 0; i < a.length; i += 3) {
        arr[i]     = a[i] * displayBucket
        arr[i + 1] = a[i + 1]
        arr[i + 2] = a[i + 2]
      }
      c._display        = arr
      c._displayBucket  = displayBucket
      c._displayRatio   = displayRatio
      c._displayVersion = dataVer
      return
    }
    let outCount = 0
    let last = NaN
    for (let i = 0; i < a.length; i += 3) {
      const ds = dsOf(a[i])
      if (ds !== last) { outCount++; last = ds }
    }
    const arr = new Float64Array(outCount * 3)
    let oi = 0
    let curDs = NaN, curBuy = 0, curSell = 0
    for (let i = 0; i < a.length; i += 3) {
      const ds  = dsOf(a[i])
      const buy = a[i + 1], sell = a[i + 2]
      if (ds !== curDs) {
        if (!Number.isNaN(curDs)) {
          arr[oi++] = curDs * displayBucket
          arr[oi++] = curBuy
          arr[oi++] = curSell
        }
        curDs   = ds
        curBuy  = buy
        curSell = sell
      } else {
        curBuy  += buy
        curSell += sell
      }
    }
    if (!Number.isNaN(curDs)) {
      arr[oi++] = curDs * displayBucket
      arr[oi++] = curBuy
      arr[oi++] = curSell
    }
    c._display        = arr
    c._displayBucket  = displayBucket
    c._displayRatio   = displayRatio
    c._displayVersion = dataVer
    return
  }

  c._display        = new Float64Array(0)
  c._displayBucket  = displayBucket
  c._displayRatio   = displayRatio
  c._displayVersion = dataVer
}

// ── Multi-slot display cache ─────────────────────────────────────────────
// WHY THIS EXISTS. `_display` is a single slot owned by whatever bucket was
// last requested, so a rebucket didn't just cost a regroup — it DESTROYED the
// previous one. Zoom in, zoom back out, and the bucket you returned to had to
// be rebuilt from _native even though it was computed seconds ago from data
// that cannot have changed. Real gestures oscillate constantly, so that was
// most of the regroup work in a session, paid repeatedly for the same answer.
//
// It is also what made precompute impossible rather than merely unbuilt:
// there was nowhere to put a display for a bucket that isn't the current one.
// Anything warmed ahead would be overwritten by the next draw at the live
// bucket. With N slots, warming becomes a two-line addition on top of this —
// but note that even with zero warming this pays for itself on any zoom that
// revisits a rung.
//
// SAFETY. A display is a pure function of (_native | _liveLevels,
// displayBucket). Closed candles never change, so a cached entry is valid
// forever; live ones bump _dataVersion on every tick, and the whole cache is
// dropped on a version change rather than per-entry — one compare instead of
// N. Unlike the sprite atlas this is GEOMETRY, so the theme cannot invalidate
// it; there are exactly two triggers, both checked here.
//
// MEMORY. _display POINTS INTO the cache (same object, not a copy), so this
// is at most MAX_DISPLAY_SLOTS arrays per candle where there was 1 — and only
// for candles actually drawn. At ~40 rows that is under 1KB per slot, so a
// visible window plus the warm margin sits in the low hundreds of KB.
//
// LRU, not FIFO: a zoom that walks out through several rungs and comes back
// would evict exactly the entries it is returning to under insertion order.
// The delete+set bump runs once per candle per frame (~78), not per cell —
// the cost that made it wrong in the sprite path is three orders of magnitude
// away here.
const MAX_DISPLAY_SLOTS = 3

// ── (ycells) Display identity = (bucket, ratio), nothing else ───────────
// A display is a pure function of (_native | _liveLevels, bucket, ratio): the
// aggregator anchors rows globally (r·db, or ratio^r anchored at 1), never at
// the lattice's own ends. The lattice-keyed path it replaces re-derived the
// spacing from lattice[0]−lattice[1] (float noise per lattice) and keyed on
// latticeId, so every rows-changed Y-glide commit re-binned every visible
// candle for an identical answer. Callers pass the canonical step (FPCC:
// dispKeyBucket / dispRatio), which is also what the projection maps with.
export const buildDisplay = (c, displayBucket, displayRatio = null) => {
  if (!c || (c.isHistorical && !c._omFootprint)) return
  const dataVer = c._dataVersion | 0
  const key = displayRatio ? `${displayBucket}|${displayRatio}` : displayBucket
  // Active slot already correct — the cheapest and by far the most common
  // case (every idle redraw, hover, crosshair, x-pan). Unchanged.
  if (c._display && c._displayBucket === displayBucket
      && c._displayRatio === displayRatio
      && c._displayVersion === dataVer) return

  let cache = c._dispCache
  if (cache) {
    if (c._dispCacheVer !== dataVer) {
      // Data moved under us: every entry describes a candle that no longer
      // exists. Drop the lot — cheaper than validating three of them, and
      // correctness does not depend on getting the per-entry accounting right.
      cache.clear()
      c._dispCacheVer = dataVer
    } else {
      const hit = cache.get(key)
      if (hit) {
        cache.delete(key)                   // LRU bump
        cache.set(key, hit)
        c._display        = hit
        c._displayBucket  = displayBucket
        c._displayRatio   = displayRatio
        c._displayVersion = dataVer
        return                                // ← the rebuild that didn't happen
      }
    }
  }

  _buildDisplayInto(c, displayBucket, displayRatio)

  // Retain, but only a real result. The no-nativeBucket branch nulls _display
  // and the cache with it, and re-adding here would resurrect a dead entry.
  if (c._display && c._displayBucket === displayBucket && c._displayRatio === displayRatio) {
    if (!cache) { cache = c._dispCache = new Map(); c._dispCacheVer = dataVer }
    if (cache.size >= MAX_DISPLAY_SLOTS) {
      const oldest = cache.keys().next().value   // insertion order = LRU order
      if (oldest !== undefined) cache.delete(oldest)
    }
    cache.set(key, c._display)
  }
}

// forEachLevel(c, (mcap, buy, sell) => … [, displayBucket, displayRatio]).
// Reads _display preferentially, falls back to live or native source.
// When displayBucket/displayRatio are passed (the live grid lattice), a
// stale _display for a coarser ratio is IGNORED — painting it onto a finer
// grid lands ink on every Nth row (patterned empties). Omit the keys for
// non-paint readers that just need any level walk.
// Empty-but-truthy _liveLevels never shadows a populated _native (restored
// candles carry both: empty Map from newLiveCandle + _native from IDB).
export const displayIsCurrent = (c, displayBucket, displayRatio) => (
  !!(c && c._display && c._display.length > 0
    && c._displayBucket === displayBucket
    && c._displayRatio === displayRatio
    && c._displayVersion === (c._dataVersion | 0))
)

export const forEachLevel = (c, fn, displayBucket, displayRatio) => {
  if (!c) return
  const latticeKeyed = displayBucket !== undefined || displayRatio !== undefined
  const useDisplay = c._display && c._display.length > 0 && (
    !latticeKeyed || displayIsCurrent(c, displayBucket, displayRatio)
  )
  if (useDisplay) {
    const a = c._display
    for (let i = 0; i < a.length; i += 3) fn(a[i], a[i + 1], a[i + 2])
    return
  }
  // Real paint lattice key (finite bucket): NEVER soft-bin native/live onto
  // the grid. That path painted bright solid buy/sell strips until
  // buildDisplay caught up (zoom/scroll/pan flash). Skip this candle's
  // levels for the frame — FPCC budgets rebuilds so the next frames fill
  // in without main-thread hitch. null,null (VP force-native) and unkeyed
  // readers still fall through below.
  if (latticeKeyed && displayBucket != null) return
  if (c._liveLevels && c._liveLevels.size > 0) {
    const b = c._nativeBucket
    for (const [step, lvl] of c._liveLevels) {
      fn(step * b, lvl.buy || 0, lvl.sell || 0)
    }
    return
  }
  if (c._native) {
    const a = c._native
    const b = c._nativeBucket
    for (let i = 0; i < a.length; i += 3) fn(a[i] * b, a[i + 1], a[i + 2])
  }
}

// forEachStorageLevel(c, (step, buy, sell, bucket) => …)
// Walks the candle's STORAGE levels (_liveLevels, else _native) — never
// _display. Anything that turns one candle's levels into ANOTHER candle's
// data (TF folds, fine→display folds) must read storage: _display holds the
// lower edges of whatever display rows were last painted, so re-keying it
// at a fine bucket stamps the old display lattice into the new candle (cells
// every Nth row once the display goes finer than that lattice).
export const forEachStorageLevel = (c, fn) => {
  if (!c || !(c._nativeBucket > 0)) return
  const b = c._nativeBucket
  if (c._liveLevels && c._liveLevels.size > 0) {
    for (const [step, lvl] of c._liveLevels) fn(step, lvl.buy || 0, lvl.sell || 0, b)
    return
  }
  const a = c._native
  if (a) for (let i = 0; i < a.length; i += 3) fn(a[i], a[i + 1], a[i + 2], b)
}

// Re-key a storage step from bucket `from` to bucket `to` (to >= from). Same
// bucket: identity (exact). Coarser target: the cell goes to the target cell
// holding its CENTRE — never its lower edge, which piles a whole coarse
// cell's volume onto one fine row. Volume conserved either way.
export const rekeyStep = (step, from, to) => (
  from === to ? step : stepIdxOf((step + 0.5) * from, to)
)

export const hasMcapLevels = (c) =>
  !!(c && (c._native || c._liveLevels || c._display))

export const candleLevelCount = (c) => {
  if (!c) return 0
  if (c._display && c._display.length > 0) return (c._display.length / 3) | 0
  if (c._liveLevels && c._liveLevels.size > 0) return c._liveLevels.size
  if (c._native)     return (c._native.length / 3) | 0
  return 0
}

export const candleTotalVolume = (c) => {
  if (!c) return 0
  if (c.isHistorical) return c.volume_tokens || 0
  if (c._liveLevels && c._liveLevels.size > 0) {
    let t = 0
    for (const lvl of c._liveLevels.values()) t += (lvl.buy || 0) + (lvl.sell || 0)
    return t
  }
  if (c._native) return c._nativeTotalVol || 0
  return 0
}

export const candleDelta = (c) => {
  if (!c) return 0
  if (c._liveLevels && c._liveLevels.size > 0) {
    let d = 0
    for (const lvl of c._liveLevels.values()) d += (lvl.buy || 0) - (lvl.sell || 0)
    return d
  }
  if (c._native) return c._nativeDelta || 0
  return 0
}

export const candleMaxLevelVolume = (c, displayBucket, displayRatio) => {
  let m = 0
  if (!c) return m
  const latticeKeyed = displayBucket !== undefined || displayRatio !== undefined
  if (c._display && c._display.length > 0
      && (!latticeKeyed || displayIsCurrent(c, displayBucket, displayRatio))) {
    const a = c._display
    for (let i = 0; i < a.length; i += 3) {
      if (a[i + 1] > m) m = a[i + 1]
      if (a[i + 2] > m) m = a[i + 2]
    }
    return m
  }
  // Match forEachLevel: paint lattice key + cold display → 0 (skip cells),
  // not native max (which lit solid R/G bars).
  if (latticeKeyed && displayBucket != null) return 0
  if (c._liveLevels && c._liveLevels.size > 0) {
    for (const lvl of c._liveLevels.values()) {
      const b = lvl.buy || 0, s = lvl.sell || 0
      if (b > m) m = b
      if (s > m) m = s
    }
    return m
  }
  if (c._native) {
    const a = c._native
    for (let i = 0; i < a.length; i += 3) {
      if (a[i + 1] > m) m = a[i + 1]
      if (a[i + 2] > m) m = a[i + 2]
    }
  }
  return m
}